/**
 * Feature E02-F06-T03 (frontend, presentation model) — pure display helpers for
 * the project event timeline. DOM-free and unit-tested without React so the
 * formatting rules the acceptance criteria pin down live in one place:
 *
 *   #1 events render newest-first, max 50 (the backend already returns
 *      ORDER BY created_at DESC LIMIT 50 — this layer never re-sorts, it only
 *      defends against a malformed/oversized payload before render);
 *   #2 each event shows event_type, a date, and a human-readable payload summary
 *      (the server sends a RU `description`; this layer picks an icon + tone for
 *      the event_type so the timeline reads at a glance);
 *   #3 created_by shows only when present (system/automatic events have no author
 *      and must render without an actor line).
 *
 * 1:1 port note: the legacy SPA loaded `project.events` (dashboard.js getProject)
 * but had no dedicated timeline section (the emails section was removed
 * 2026-04-26), so there is no legacy markup to mirror — this is the new section.
 * The event_type → label/payload mapping also exists server-side
 * (`services/projectEventsTimeline.describeEvent`), but that returns RU; the
 * English-only UI (fidelity parity FP-02) re-renders it here from event_type +
 * payload, and the client also adds the visual affordances (icon/tone) and the
 * date string. Keep `describeEvent` here in sync with the backend copy.
 */
import { PROJECT_EVENTS_LIMIT, type ProjectEventDTO, type ProjectEventPayload } from '@eer/shared';

/** Visual tone buckets for a timeline dot (maps to a Tailwind colour class set). */
export type TimelineTone = 'create' | 'ai' | 'email' | 'send' | 'neutral';

/** A timeline dot's icon (Font Awesome class) + tone, derived from event_type. */
export interface TimelineGlyph {
  /** Font Awesome icon class (`fa-solid fa-…`). */
  readonly icon: string;
  /** Tone bucket → colour classes (see TIMELINE_TONE_CLASS in the view). */
  readonly tone: TimelineTone;
}

/**
 * Map an `event_type` to its dot glyph. Covers the canonical five plus the legacy
 * literals the backend humanizer also recognizes; an unknown type falls back to a
 * neutral dot so a future event still renders (acceptance #2: graceful display).
 */
export function eventGlyph(eventType: string): TimelineGlyph {
  switch (eventType) {
    case 'project_created':
    case 'project_created_from_email':
    case 'project_created_manual':
      return { icon: 'fa-solid fa-folder-plus', tone: 'create' };
    case 'ai_analysis_completed':
      return { icon: 'fa-solid fa-wand-magic-sparkles', tone: 'ai' };
    case 'kickoff_enrichment_completed':
      return { icon: 'fa-solid fa-list-check', tone: 'ai' };
    case 'email_linked':
      return { icon: 'fa-solid fa-link', tone: 'email' };
    case 'email_unlinked':
      return { icon: 'fa-solid fa-link-slash', tone: 'email' };
    case 'pre_kickoff_email_sent':
    case 'stage5_email_sent':
    case 'final_package_sent':
    case 'client_followup_email_sent':
    case 'sourcing_sheet_email_sent':
    case 'internal_notification_sent':
      return { icon: 'fa-solid fa-paper-plane', tone: 'send' };
    case 'project_updated':
      return { icon: 'fa-solid fa-pen', tone: 'neutral' };
    default:
      return { icon: 'fa-solid fa-circle-dot', tone: 'neutral' };
  }
}

/**
 * Defensive client-side ordering/cap: the backend already returns newest-first,
 * ≤50, but if a payload arrives unsorted or longer (a drifted/older endpoint),
 * normalize it so the UI honours acceptance #1 regardless. Stable: equal
 * timestamps keep their server order.
 */
export function orderedTimeline(events: readonly ProjectEventDTO[]): ProjectEventDTO[] {
  const sorted = [...events].sort((a, b) => {
    const ta = Date.parse(a.created_at);
    const tb = Date.parse(b.created_at);
    if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
    return tb - ta; // DESC — newest first
  });
  return sorted.slice(0, PROJECT_EVENTS_LIMIT);
}

/**
 * Absolute timestamp for a timeline row (locale date + short time). Returns an
 * empty string for an unparseable value so the UI never shows `Invalid Date`.
 */
export function formatEventTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * Author line for a row, or `null` when the event has no actor (system/automatic
 * events — acceptance #3: created_by shows only if present). Trims so an
 * all-whitespace value is treated as absent.
 */
export function authorLabel(createdBy: string | null): string | null {
  if (createdBy == null) return null;
  const trimmed = createdBy.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * English labels for the canonical + legacy event_type literals.
 *
 * The web UI is English-only (fidelity parity FP-02): the backend still sends a
 * RU `description`, but the timeline renders its own English line from
 * `event_type + payload` so no Russian leaks into the interface. This mirrors the
 * backend humanizer (`packages/api/.../projectEventsTimeline.describeEvent`),
 * translated — keep the two in sync if event copy changes.
 */
const EVENT_LABELS: Record<string, string> = {
  project_created: 'Project created',
  project_created_from_email: 'Project created from email',
  project_created_manual: 'Project created manually',
  project_updated: 'Project updated',
  ai_analysis_completed: 'AI analysis completed',
  kickoff_enrichment_completed: 'Kickoff enrichment completed',
  pre_kickoff_email_sent: 'Pre-kickoff email sent',
  email_linked: 'Email linked',
  email_unlinked: 'Email unlinked',
  stage5_email_sent: 'Stage 5 email sent',
  final_package_sent: 'Final package sent',
  client_followup_email_sent: 'Client follow-up sent',
  sourcing_sheet_email_sent: 'Sourcing sheet sent',
  internal_notification_sent: 'Internal notification sent',
};

/** Fallback humanizer for an unmapped event_type ('foo_bar_done' → 'Foo bar done'). */
function humanizeType(eventType: string): string {
  const words = eventType.replace(/_/g, ' ').trim();
  if (!words) return 'Event';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Trim a string to `max` chars with an ellipsis (UI safety). */
function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

/**
 * Render a human-readable English description from (event_type + payload).
 * Pure; covers the canonical events plus a graceful fallback for any other
 * literal (label from the map, else the raw event_type humanized).
 */
export function describeEvent(eventType: string, payload: ProjectEventPayload): string {
  const base = EVENT_LABELS[eventType] ?? humanizeType(eventType);

  switch (eventType) {
    case 'project_created':
    case 'project_created_from_email': {
      const source = payload.source;
      if (source === 'manual') return 'Project created manually';
      const emailId = payload.emailId ?? payload.email_id;
      return emailId !== undefined ? `${base} (email #${String(emailId)})` : base;
    }
    case 'project_created_manual':
      return base;
    case 'ai_analysis_completed': {
      const parts: string[] = [];
      if (payload.classification) parts.push(`classification: ${String(payload.classification)}`);
      if (payload.recommendation) parts.push(`recommendation: ${String(payload.recommendation)}`);
      return parts.length > 0 ? `${base} (${parts.join(', ')})` : base;
    }
    case 'pre_kickoff_email_sent': {
      const option = payload.option;
      return option ? `${base} (option ${String(option)})` : base;
    }
    case 'email_linked': {
      const emailId = payload.emailId ?? payload.email_id;
      const summary = typeof payload.summary === 'string' ? payload.summary : '';
      const suffix = emailId !== undefined ? ` (email #${String(emailId)})` : '';
      return summary ? `${base}${suffix}: ${truncate(summary, 120)}` : `${base}${suffix}`;
    }
    case 'email_unlinked': {
      const emailId = payload.emailId ?? payload.email_id;
      return emailId !== undefined ? `${base} (email #${String(emailId)})` : base;
    }
    default:
      return base;
  }
}

/**
 * The line the UI renders as the event description. Computed on the client in
 * English (fidelity parity FP-02 — the UI must not show the backend's RU
 * `description`) from `event_type + payload`; never blank (falls back to the raw
 * event_type, acceptance #2).
 */
export function eventDescription(event: ProjectEventDTO): string {
  const line = describeEvent(event.event_type, event.payload).trim();
  return line.length > 0 ? line : event.event_type;
}
